import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';

interface Strategy {
  id: number;
  name: string;
  description: string;
  type: 'automated' | 'manual' | 'copy';
  status: 'active' | 'paused' | 'stopped';
  performance: string;
  risk: 'Low' | 'Medium' | 'High';
  timeframe: string;
  capital: number;
  trades: number;
  winRate: number;
  creator?: string;
  pnl?: string;
  group?: string;
}

interface Order {
  id: number;
  token: string;
  amount: number;
  price: number;
  side: 'buy' | 'sell';
  status: 'pending' | 'filled' | 'cancelled';
  timestamp: string;
  pnl?: number;
}

const StrategiesPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'my' | 'marketplace' | 'orders'>('my');
  const [filterType, setFilterType] = useState<string>('all');
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formType, setFormType] = useState<'automated' | 'manual' | 'copy'>('automated');
  const [formRisk, setFormRisk] = useState<'Low' | 'Medium' | 'High'>('Low');
  const [formTimeframe, setFormTimeframe] = useState('1h-4h');
  const [formCapital, setFormCapital] = useState<number>(1000);

  // Mock data for orders
  useEffect(() => {
    const mockOrders: Order[] = [
      {
        id: 1,
        token: 'AVNT',
        amount: 25,
        price: 0.85,
        side: 'buy',
        status: 'filled',
        timestamp: '2025-09-27 22:30:15',
        pnl: 12.5
      },
      {
        id: 2,
        token: 'VVV',
        amount: 50,
        price: 1.20,
        side: 'buy',
        status: 'pending',
        timestamp: '2025-09-27 22:25:30'
      },
      {
        id: 3,
        token: 'BTC',
        amount: 0.001,
        price: 43250,
        side: 'sell',
        status: 'filled',
        timestamp: '2025-09-27 22:20:45',
        pnl: -5.2
      }
    ];
    setOrders(mockOrders);
  }, []);
  
  // Strategy templates for quick start
  const strategyTemplates = [
    {
      id: 1,
      name: 'Buy + Profit/Loss',
      description: "If anyone in #copytrade buys a token for >$500, buy me $50 of it. Then sell 20% for 10% gain, sell 20% for 25% gain, sell 20% for 50% gain, sell 20% for 75% gain. Set stop loss at -40%. Marketcap between $50k and $500M.",
      type: 'copy' as const,
      risk: 'Medium' as const,
      timeframe: 'Real-time',
      performance: '+23.4%',
      trades: 45,
      winRate: 78.2,
      group: '#copytrade'
    },
    {
      id: 2,
      name: 'Double Trigger',
      description: "If any 2 people in #copytrade buys >$1000 of a token within 10 minutes of each other, buy me $100 of it, and then sell 50% for 30% profit, sell 50% for 100% profit. Set stop loss at -30%. Minimum marketcap of $50k.",
      type: 'copy' as const,
      risk: 'High' as const,
      timeframe: '10min window',
      performance: '+41.2%',
      trades: 23,
      winRate: 65.2,
      group: '#copytrade'
    },
    {
      id: 3,
      name: 'Buy + Ladder +',
      description: "If anyone in #copytrade >$500, buy me when they sell. Sell 20% for 25% gain, sell 20% for 50% gain, sell 20% for 75% gain, sell 20% for 100% gain. Set stop loss at -40%. Marketcap between $25k and $500M.",
      type: 'copy' as const,
      risk: 'Medium' as const,
      timeframe: 'Real-time',
      performance: '+18.7%',
      trades: 67,
      winRate: 71.6,
      group: '#copytrade'
    }
  ];

  // User strategies
  const initialMyStrategies: Strategy[] = [
    {
      id: 1,
      name: 'DCA Bitcoin Strategy',
      description: 'Dollar-cost averaging into Bitcoin with weekly purchases',
      type: 'automated',
      status: 'active',
      performance: '+15.2%',
      risk: 'Low',
      timeframe: 'Long-term',
      capital: 5000,
      trades: 24,
      winRate: 87.5,
      pnl: '+$760'
    },
    {
      id: 2,
      name: 'Scalping ETHUSDT',
      description: 'High-frequency scalping on Ethereum with 1-minute intervals',
      type: 'automated',
      status: 'paused',
      performance: '+8.7%',
      risk: 'High',
      timeframe: '1m-5m',
      capital: 2500,
      trades: 156,
      winRate: 62.8,
      pnl: '+$217'
    }
  ];
  const [myStrategies, setMyStrategies] = useState<Strategy[]>(initialMyStrategies);

  // Marketplace strategies
  const marketplaceStrategies: Strategy[] = [
    {
      id: 4,
      name: 'AI Momentum Hunter',
      description: 'Machine learning algorithm that identifies momentum patterns',
      type: 'copy',
      status: 'active',
      performance: '+34.5%',
      risk: 'Medium',
      timeframe: '15m-1h',
      capital: 0,
      trades: 89,
      winRate: 71.9,
      creator: 'QuantMaster',
      pnl: '+$1,245'
    },
    {
      id: 5,
      name: 'Grid Trading Pro',
      description: 'Advanced grid trading with dynamic range adjustment',
      type: 'copy',
      status: 'active',
      performance: '+19.8%',
      risk: 'Low',
      timeframe: '1h-4h',
      capital: 0,
      trades: 234,
      winRate: 84.2,
      creator: 'GridBot',
      pnl: '+$892'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-400/20';
      case 'paused': return 'text-yellow-400 bg-yellow-400/20';
      case 'stopped': return 'text-red-400 bg-red-400/20';
      case 'pending': return 'text-blue-400 bg-blue-400/20';
      case 'filled': return 'text-green-400 bg-green-400/20';
      case 'cancelled': return 'text-gray-400 bg-gray-400/20';
      default: return 'text-green-400 bg-green-400/20';
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Low': return 'text-green-400';
      case 'Medium': return 'text-yellow-400';
      case 'High': return 'text-red-400';
      default: return 'text-green-400';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'automated': return 'text-blue-400';
      case 'manual': return 'text-purple-400';
      case 'copy': return 'text-orange-400';
      default: return 'text-green-400';
    }
  };

  const getSideColor = (side: string) => {
    switch (side) {
      case 'buy': return 'text-green-400';
      case 'sell': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const filteredStrategies = (activeTab === 'my' ? myStrategies : marketplaceStrategies).filter(strategy => 
    filterType === 'all' || strategy.type === filterType
  );

  const fetchOrders = async () => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  useEffect(() => {
    if (activeTab === 'orders') {
      fetchOrders();
    }
  }, [activeTab]);

  return (
    <div className="min-h-full bg-[#0a0a1a] text-green-400 font-mono p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-green-400 mb-2">Trading Strategies</h1>
          <div className="text-green-400/70 text-sm">
            Create, manage, and discover profitable trading strategies
          </div>
        </div>
        <div className="flex space-x-3">
          <Button 
            onClick={() => navigate('/chat')}
            className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 hover:from-green-500/30 hover:to-emerald-500/30 text-green-400 border border-green-500/30 px-6 py-2 flex items-center space-x-2"
          >
            <span>🤖</span>
            <span>AI Generate</span>
          </Button>
          <Button 
            onClick={() => setShowCreate(true)} 
            className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 hover:from-purple-500/30 hover:to-blue-500/30 text-white border border-purple-500/30 px-6 py-2"
          >
            + Create Strategy
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/30 hover:border-green-400/50 transition-all">
          <div className="text-green-400/70 text-sm mb-1">Active Strategies</div>
          <div className="text-2xl font-bold text-green-400">{myStrategies.filter(s => s.status === 'active').length}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/30 hover:border-blue-400/50 transition-all">
          <div className="text-blue-400/70 text-sm mb-1">Total Performance</div>
          <div className="text-2xl font-bold text-blue-400">+18.7%</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/30 hover:border-purple-400/50 transition-all">
          <div className="text-purple-400/70 text-sm mb-1">Total Capital</div>
          <div className="text-2xl font-bold text-purple-400">$17.5K</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/30 hover:border-orange-400/50 transition-all">
          <div className="text-orange-400/70 text-sm mb-1">Win Rate</div>
          <div className="text-2xl font-bold text-orange-400">76.0%</div>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between mb-6">
        {/* Tab Navigation */}
        <div className="flex space-x-1">
          <Button
            onClick={() => setActiveTab('my')}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              activeTab === 'my'
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400'
            }`}
          >
            My Strategies ({myStrategies.length})
          </Button>
          <Button
            onClick={() => setActiveTab('marketplace')}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              activeTab === 'marketplace'
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400'
            }`}
          >
            Marketplace
          </Button>
          <Button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-2 text-sm rounded transition-colors ${
              activeTab === 'orders'
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-transparent text-green-400/70 hover:bg-green-500/10 hover:text-green-400'
            }`}
          >
            Orders ({orders.length})
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-[#0d0d2a] border border-green-500/30 text-green-400 text-sm rounded px-3 py-1 focus:border-green-400 focus:outline-none"
          >
            <option value="all">All Types</option>
            <option value="automated">Automated</option>
            <option value="manual">Manual</option>
            <option value="copy">Copy Trading</option>
          </select>
          <Input
            placeholder="Search strategies..."
            className="w-48 bg-transparent border-green-500/30 text-green-400 placeholder-green-400/50 focus:border-green-400"
          />
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'my' && (
        <div className="space-y-6">
          {/* Strategy Templates */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-full flex items-center justify-center mr-3">
                <span className="text-lg">🤖</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-green-400">Create your first strategy</h2>
                <p className="text-green-400/70 text-sm">Get Started</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-4 mb-6">
              <p className="text-green-300 text-sm">
                Let's build an auto-trading strategy based on #copytrade group. Choose a template or write your own.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {strategyTemplates.map((template) => (
                <Card key={template.id} className="p-6 bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border-green-500/30 hover:border-green-400/50 hover:bg-green-500/5 transition-all duration-300 transform hover:scale-[1.02]">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-green-300 font-semibold mb-1 text-lg">{template.name}</h3>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getTypeColor(template.type)} bg-opacity-20`}>
                          {template.type.toUpperCase()}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getRiskColor(template.risk)}`}>
                          {template.risk} RISK
                        </span>
                      </div>
                      <div className="text-xs text-green-500/70 flex items-center">
                        <span className="mr-1">👥</span>
                        {template.group}
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-green-300 font-bold text-xl">{template.performance}</div>
                      <div className="text-xs text-green-500/70">Performance</div>
                    </div>
                  </div>

                  <p className="text-green-400/70 text-sm mb-4 leading-relaxed">{template.description}</p>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-green-500/5 p-3 rounded-lg border border-green-500/10">
                      <div className="text-green-400/70 text-xs mb-1 flex items-center">
                        <span className="mr-1">⏱️</span>
                      Timeframe
                      </div>
                      <div className="text-green-400 text-sm">{template.timeframe}</div>
                    </div>
                    <div className="bg-green-500/5 p-3 rounded-lg border border-green-500/10">
                      <div className="text-green-400/70 text-xs mb-1 flex items-center">
                        <span className="mr-1">🎯</span>
                        Win Rate
                      </div>
                      <div className="text-green-400 text-sm font-medium">{template.winRate}%</div>
                    </div>
                  </div>

                  <Button 
                    onClick={() => navigate('/chat')}
                    className="w-full bg-gradient-to-r from-green-500/20 to-blue-500/20 hover:from-green-500/30 hover:to-blue-500/30 text-green-400 border border-green-500/30 hover:border-green-400/50 transition-all"
                  >
                    🤖 Use AI Template
                  </Button>
                </Card>
              ))}
            </div>
          </div>

          {/* My Strategies */}
          <div>
            <h2 className="text-xl font-bold text-green-400 mb-4">My Active Strategies</h2>
            <div className="grid lg:grid-cols-2 gap-6">
              {filteredStrategies.map((strategy) => (
                <Card key={strategy.id} className="p-6 bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border-green-500/30 hover:border-green-400/50 hover:bg-green-500/5 transition-all duration-300 transform hover:scale-[1.02]">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-green-300 font-semibold mb-1 text-lg">{strategy.name}</h3>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getTypeColor(strategy.type)} bg-opacity-20`}>
                          {strategy.type.toUpperCase()}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(strategy.status)}`}>
                          {strategy.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-green-300 font-bold text-xl">{strategy.performance}</div>
                      <div className="text-xs text-green-500/70">Performance</div>
                    </div>
                  </div>

                  <p className="text-green-400/70 text-sm mb-4 leading-relaxed">{strategy.description}</p>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-green-500/5 p-3 rounded-lg border border-green-500/10">
                      <div className="text-green-400/70 text-xs mb-1 flex items-center">
                        <span className="mr-1">💰</span>
                        P&L
                      </div>
                      <div className="text-green-400 text-sm font-medium">{strategy.pnl}</div>
                    </div>
                    <div className="bg-green-500/5 p-3 rounded-lg border border-green-500/10">
                      <div className="text-green-400/70 text-xs mb-1 flex items-center">
                        <span className="mr-1">📊</span>
                        Trades
                      </div>
                      <div className="text-green-400 text-sm font-medium">{strategy.trades}</div>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    {strategy.status === 'active' ? (
                      <Button size="sm" variant="outline" className="flex-1 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 hover:border-yellow-400/50 transition-all">
                        ⏸️ Pause
                      </Button>
                    ) : (
                      <Button size="sm" className="flex-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 hover:border-green-400/50 transition-all">
                        ▶️ Start
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="border-green-500/30 text-green-400/70 hover:bg-green-500/10 hover:border-green-400/50 transition-all">
                      ✏️
                    </Button>
                    <Button size="sm" variant="outline" className="border-green-500/30 text-green-400/70 hover:bg-green-500/10 hover:border-green-400/50 transition-all">
                      📈
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'marketplace' && (
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6 pb-6">
          {filteredStrategies.map((strategy) => (
            <Card key={strategy.id} className="p-6 bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border-green-500/30 hover:border-green-400/50 hover:bg-green-500/5 transition-all duration-300 transform hover:scale-[1.02]">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-green-300 font-semibold mb-1 text-lg">{strategy.name}</h3>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getTypeColor(strategy.type)} bg-opacity-20`}>
                      {strategy.type.toUpperCase()}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(strategy.status)}`}>
                      {strategy.status.toUpperCase()}
                    </span>
                  </div>
                  {strategy.creator && (
                    <div className="text-xs text-green-500/70 flex items-center">
                      <span className="mr-1">👤</span>
                      by {strategy.creator}
                    </div>
                  )}
                </div>
                <div className="text-right ml-4">
                  <div className="text-green-300 font-bold text-xl">{strategy.performance}</div>
                  <div className="text-xs text-green-500/70">Performance</div>
                </div>
              </div>

              <p className="text-green-400/70 text-sm mb-4 leading-relaxed">{strategy.description}</p>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-green-500/5 p-3 rounded-lg border border-green-500/10">
                  <div className="text-green-400/70 text-xs mb-1 flex items-center">
                    <span className="mr-1">⚠️</span>
                    Risk Level
                  </div>
                  <div className={`text-sm font-medium ${getRiskColor(strategy.risk)}`}>{strategy.risk}</div>
                </div>
                <div className="bg-green-500/5 p-3 rounded-lg border border-green-500/10">
                  <div className="text-green-400/70 text-xs mb-1 flex items-center">
                    <span className="mr-1">⏱️</span>
                    Timeframe
                  </div>
                  <div className="text-green-400 text-sm">{strategy.timeframe}</div>
                </div>
                <div className="bg-green-500/5 p-3 rounded-lg border border-green-500/10">
                  <div className="text-green-400/70 text-xs mb-1 flex items-center">
                    <span className="mr-1">🎯</span>
                    Win Rate
                  </div>
                  <div className="text-green-400 text-sm font-medium">{strategy.winRate}%</div>
                </div>
                <div className="bg-green-500/5 p-3 rounded-lg border border-green-500/10">
                  <div className="text-green-400/70 text-xs mb-1 flex items-center">
                    <span className="mr-1">📊</span>
                    Trades
                  </div>
                  <div className="text-green-400 text-sm font-medium">{strategy.trades}</div>
                </div>
              </div>

              <div className="flex space-x-2">
                <Button size="sm" className="flex-1 bg-gradient-to-r from-green-500/20 to-blue-500/20 hover:from-green-500/30 hover:to-blue-500/30 text-green-400 border border-green-500/30 hover:border-green-400/50 transition-all">
                  📋 Copy Strategy
                </Button>
                <Button size="sm" variant="outline" className="border-green-500/30 text-green-400/70 hover:bg-green-500/10 hover:border-green-400/50 transition-all">
                  🔍 Details
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-green-400">Recent Orders</h2>
            <Button 
              onClick={fetchOrders}
              disabled={isLoading}
              className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30"
            >
              {isLoading ? '⏳' : '🔄'} Refresh
            </Button>
          </div>

          <div className="grid gap-4">
            {orders.map((order) => (
              <Card key={order.id} className="p-4 bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border-green-500/30 hover:border-green-400/50 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-3 h-3 rounded-full ${getSideColor(order.side)} bg-current`}></div>
                    <div>
                      <div className="text-green-300 font-medium">{order.token}</div>
                      <div className="text-green-400/70 text-sm">{order.side.toUpperCase()} • {order.amount} tokens</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-green-400 font-medium">${order.price.toLocaleString()}</div>
                    <div className="text-green-400/70 text-sm">{order.timestamp}</div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(order.status)}`}>
                      {order.status.toUpperCase()}
                    </span>
                    {order.pnl && (
                      <div className={`text-sm font-medium mt-1 ${order.pnl > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {order.pnl > 0 ? '+' : ''}{order.pnl}%
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {filteredStrategies.length === 0 && activeTab !== 'orders' && (
        <div className="text-center py-12">
          <div className="text-green-400/50 text-lg mb-2">No strategies found</div>
          <div className="text-green-400/30 text-sm">
            {activeTab === 'my' ? 'Create your first strategy to get started' : 'No strategies match your filters'}
          </div>
        </div>
      )}

      {/* Create Strategy Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowCreate(false)} />
          <div className="relative z-10 w-full max-w-2xl mx-4 bg-gradient-to-br from-[#0d0d2a] to-[#0a0a1a] border border-green-500/30 rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-green-300">Create Strategy</h3>
              <button onClick={() => setShowCreate(false)} className="text-green-400/70 hover:text-green-400">✕</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-green-400/70 mb-1">Name</div>
                <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="e.g., Momentum Breakout" className="bg-transparent border-green-500/30 text-green-300 placeholder-green-500/50" />
              </div>
              <div>
                <div className="text-sm text-green-400/70 mb-1">Type</div>
                <select value={formType} onChange={(e) => setFormType(e.target.value as any)} className="w-full bg-[#0d0d2a] border border-green-500/30 text-green-300 rounded px-3 py-2">
                  <option value="automated">Automated</option>
                  <option value="manual">Manual</option>
                  <option value="copy">Copy</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <div className="text-sm text-green-400/70 mb-1">Description</div>
                <textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} rows={4} placeholder="Describe entry/exit, risk, markets..." className="w-full bg-transparent border border-green-500/30 text-green-300 rounded px-3 py-2 placeholder-green-500/50" />
              </div>
              <div>
                <div className="text-sm text-green-400/70 mb-1">Risk</div>
                <select value={formRisk} onChange={(e) => setFormRisk(e.target.value as any)} className="w-full bg-[#0d0d2a] border border-green-500/30 text-green-300 rounded px-3 py-2">
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
              <div>
                <div className="text-sm text-green-400/70 mb-1">Timeframe</div>
                <Input value={formTimeframe} onChange={(e) => setFormTimeframe(e.target.value)} placeholder="e.g., 15m-1h" className="bg-transparent border-green-500/30 text-green-300 placeholder-green-500/50" />
              </div>
              <div>
                <div className="text-sm text-green-400/70 mb-1">Capital ($)</div>
                <Input type="number" value={formCapital} onChange={(e) => setFormCapital(Number(e.target.value))} className="bg-transparent border-green-500/30 text-green-300" />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button onClick={() => setShowCreate(false)} variant="outline" className="border-green-500/30 text-green-400/70 hover:bg-green-500/10">Cancel</Button>
              <Button
                onClick={() => {
                  const newStrategy: Strategy = {
                    id: Date.now(),
                    name: formName || 'Untitled Strategy',
                    description: formDescription || '—',
                    type: formType,
                    status: 'active',
                    performance: '+0.0%',
                    risk: formRisk,
                    timeframe: formTimeframe,
                    capital: formCapital,
                    trades: 0,
                    winRate: 0,
                    pnl: '$0'
                  };
                  setMyStrategies((prev) => [newStrategy, ...prev]);
                  setShowCreate(false);
                  setActiveTab('my');
                  // reset
                  setFormName('');
                  setFormDescription('');
                  setFormType('automated');
                  setFormRisk('Low');
                  setFormTimeframe('1h-4h');
                  setFormCapital(1000);
                }}
                className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30"
              >
                Save Strategy
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategiesPage;